// T-29 — list catalog tours the current user can start (role + mode + caps).

import type { AuthContextValue } from "@/contexts/AuthContext";
import type { PrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import type { HasCapabilityFn } from "@/lib/capabilities/capabilities";
import {
  resolveFireflyTour,
  type CanAccessPrimaryModeFn,
} from "./resolveFireflyTour";
import type { FireflyTourArtifact } from "./tourTypes";

export type AccessibleFireflyTour = {
  artifact: FireflyTourArtifact;
  targetMode: PrimaryMode;
};

export type ListAccessibleFireflyToursInput = {
  artifacts: readonly FireflyTourArtifact[];
  auth: AuthContextValue;
  currentMode: PrimaryMode;
  canAccessPrimaryMode: CanAccessPrimaryModeFn;
  hasCapability?: HasCapabilityFn;
};

/** Human label for catalog rows — derived from tour id (no separate title field). */
export function fireflyTourDisplayTitle(artifact: FireflyTourArtifact): string {
  const raw = artifact.id.replace(/^tour-/, "").trim();
  if (!raw) return "Tour";
  return raw
    .split("-")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

/**
 * Returns shipped tours the user can start right now (same gates as
 * `resolveFireflyTour` with an explicit `tourId`).
 */
export function listAccessibleFireflyTours(
  input: ListAccessibleFireflyToursInput,
): AccessibleFireflyTour[] {
  const out: AccessibleFireflyTour[] = [];
  for (const artifact of input.artifacts) {
    const result = resolveFireflyTour({
      tourId: artifact.id,
      artifacts: input.artifacts,
      auth: input.auth,
      currentMode: input.currentMode,
      canAccessPrimaryMode: input.canAccessPrimaryMode,
      hasCapability: input.hasCapability,
    });
    if (result.kind === "start") {
      out.push({ artifact: result.artifact, targetMode: result.targetMode });
    }
  }
  return out;
}
